import LoadingTextSkeleton from '@components/skeleton/LoadingTextSkeleton';
import { AutoTrackingZoomRatio, DetectionObject } from '@EcamModel/model/SP111AutoTracking';
import { Divider, Stack, Typography } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2/Grid2';
import { useCameraDetailContext } from '@pages/cameras/hook/CameraDetailsProvider';
import useMileSightSP111, { SectionsCollapseSP111 } from '@pages/cameras/hook/useMilesightSP111';
import color from '@theme/Colors';
import { ReactNode, useEffect } from 'react';
import { transformScheduleData } from 'src/helpers';
import {
    DataSectionHasSubItem,
    SectionHasSubItemHeader,
    SectionHasSubItemProps,
    SectionNotSubItem,
} from '../../../ptz/milesight/components/BasicInfoMilesightCameraPTZ';

const detectionObjectMapping: { [key in DetectionObject]: string } = {
    [DetectionObject.None]: 'None',
    [DetectionObject.OnlyPerson]: 'Person',
    [DetectionObject.OnlyVehicle]: 'Vehicle',
    [DetectionObject.PersonAndVehicle]: 'Person and Vehicle',
};

const zoomModeMapping: { [key in AutoTrackingZoomRatio]: string } = {
    [AutoTrackingZoomRatio.Auto]: 'Auto mode',
    [AutoTrackingZoomRatio.Customize]: 'Customize',
};

type Props = {
    isOpenCollapse: { [key in SectionsCollapseSP111]: boolean };
    handleToggleCollapse: (section: SectionsCollapseSP111) => void;
};

export default function AutoTrackingMilesightsCameraSP111(props: Props) {
    const handleEdit = () => {
        console.log('Edit button clicked');
    };
    const { camera, filteredCamera } = useCameraDetailContext();
    const { isOpenCollapse, handleToggleCollapse } = props;
    const { SP111AutoTracking, isLoadingSP111AutoTracking } = useMileSightSP111({
        filteredCamera,
        cameraSP111: camera?.SP111CameraId!,
    });

    const statusList_AutoTrackingHeader: DataSectionHasSubItem[] = [
        {
            label: 'Enable:',
            value: (
                <LoadingTextSkeleton
                    conditions={isLoadingSP111AutoTracking}
                    value={SP111AutoTracking?.TrackEnable! ? 'Yes' : 'No'}
                    loadingStyle={{
                        width: '50%',
                    }}
                />
            ),
        },
        {
            label: 'Report to motion detection:',
            value: (
                <LoadingTextSkeleton
                    conditions={isLoadingSP111AutoTracking}
                    value={SP111AutoTracking?.ReportMotion! ? 'Yes' : 'No'}
                    loadingStyle={{
                        width: '50%',
                    }}
                />
            ),
        },
    ];

    const statusList_AutoTracking_BasicSettings: DataSectionHasSubItem[] = [
        {
            label: 'Show tracking:',
            value: (
                <LoadingTextSkeleton
                    conditions={isLoadingSP111AutoTracking}
                    value={SP111AutoTracking?.ShowAutoEnable! ? 'Yes' : 'No'}
                    loadingStyle={{
                        width: '50%',
                    }}
                />
            ),
        },
        {
            label: 'Sensitivity:',
            value: (
                <LoadingTextSkeleton
                    conditions={isLoadingSP111AutoTracking}
                    value={SP111AutoTracking?.Sensitivity!}
                    loadingStyle={{
                        width: '50%',
                    }}
                />
            ),
        },
        {
            label: 'Max. tracking time:',
            value: (
                <LoadingTextSkeleton
                    conditions={isLoadingSP111AutoTracking}
                    value={`${SP111AutoTracking?.MaxTime!} (5-300s)`}
                    loadingStyle={{
                        width: '50%',
                    }}
                />
            ),
        },
        {
            label: 'Tracking zoom ratio:',
            value: (
                <LoadingTextSkeleton
                    conditions={isLoadingSP111AutoTracking}
                    value={zoomModeMapping[SP111AutoTracking?.ZoomMode!]}
                    loadingStyle={{
                        width: '50%',
                    }}
                />
            ),
        },
        {
            label: 'Detection object:',
            value: (
                <LoadingTextSkeleton
                    conditions={isLoadingSP111AutoTracking}
                    value={detectionObjectMapping[SP111AutoTracking?.Object!]}
                    loadingStyle={{
                        width: '50%',
                    }}
                />
            ),
        },
    ];

    const dataSectionHasSubItems: ReactNode | DataSectionHasSubItem[] = transformScheduleData(
        SP111AutoTracking?.ScheduleList!,
        (dayName, schedule) => ({
            label: `${dayName}:`,
            value: schedule,
        })
    );

    const sectionHasSubItemProps: SectionHasSubItemProps = {
        title: '',
        data: dataSectionHasSubItems,
    };

    return (
        <Stack id="autoTracking_BasicSettings" border={'1px solid #E4E7EC'} borderRadius={2} flex={1}>
            {/* Basic Information */}
            <SectionHasSubItemHeader id="" title="Auto tracking" onEdit={handleEdit} />

            <Divider
                orientation="vertical"
                sx={{
                    borderBottomWidth: 1,
                    borderColor: '#E4E7EC',
                    mx: 2,
                }}
            />

            <Stack width={'100%'} padding={'16px 16px'} bgcolor={color.white} gap={2}>
                {statusList_AutoTrackingHeader.map((item, index) => {
                    return (
                        <Grid key={index} xs={12} container>
                            <Grid xs={5}>
                                <Typography>{item.label}</Typography>
                            </Grid>
                            <Grid xs={7}>{item.value}</Grid>
                        </Grid>
                    );
                })}
            </Stack>

            {/* Basic Settings */}
            <SectionNotSubItem
                header="Basic settings"
                statusList={statusList_AutoTracking_BasicSettings}
                isOpen={isOpenCollapse.autoTracking_BasicSettings}
                onToggleCollapse={() => {
                    handleToggleCollapse(SectionsCollapseSP111.AutoTracking_BasicSettings);
                }}
            />

            {/* Schedule Settings */}
            <SectionNotSubItem
                name="autoTracking_ScheduleSettings"
                header="Schedule settings"
                statusList={sectionHasSubItemProps.data}
                isOpen={isOpenCollapse.autoTracking_ScheduleSettings}
                onToggleCollapse={() => {
                    handleToggleCollapse(SectionsCollapseSP111.AutoTracking_ScheduleSettings);
                }}
            />
        </Stack>
    );
}
